import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  FileText, Shield,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// HrPoliciesAdminScreen — /hr/policies
// T-121 phase 1, chunk A — manager/director admin surface for the policy
// registry. Field staff never see this; their self-serve view is at /policies.
//
// This screen shows:
//   • Every active policy in the IMS register
//   • For each, the assigned headcount + ack rate (acked / total assigned)
//   • The current version label + category
//   • Quick-glance status pills (every-acked / partially-acked / no-acks)
//
// Publish-new-version, force-reack, retire and CSV export land in chunk C.
// =============================================================================

interface PolicyRow {
  policy_id:               string;
  policy_code:             string;
  display_name:            string;
  policy_category:         string | null;
  current_version_label:   string | null;
  total_assigned:          number;
  acked_current:           number;
  pending_count:           number;
}

// -----------------------------------------------------------------------------

export default function HrPoliciesAdminScreen() {
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");

  // Aggregates from v_policy_pending_for_user — one row per (policy, user)
  // already, with status. Group it client-side to compute ack rates per
  // policy. Cheap because the policy register is small.
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["hr_policy_admin"],
    enabled:  canView,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from("v_policy_pending_for_user")
        .select(`
          policy_id, policy_code, display_name, policy_category,
          current_version_label, ack_status
        `);
      if (error) throw error;

      // Group by policy_id
      const m = new Map<string, PolicyRow>();
      for (const r of (data ?? []) as any[]) {
        const k = r.policy_id;
        const existing = m.get(k);
        if (existing) {
          existing.total_assigned += 1;
          if (r.ack_status === "current") existing.acked_current += 1;
          else                            existing.pending_count += 1;
        } else {
          m.set(k, {
            policy_id:             k,
            policy_code:           r.policy_code,
            display_name:          r.display_name,
            policy_category:       r.policy_category,
            current_version_label: r.current_version_label,
            total_assigned:        1,
            acked_current:         r.ack_status === "current" ? 1 : 0,
            pending_count:         r.ack_status !== "current" ? 1 : 0,
          });
        }
      }
      return Array.from(m.values()).sort((a, z) =>
        a.policy_code.localeCompare(z.policy_code)
      );
    },
  });

  const totals = useMemo(() => {
    let totalAcked = 0, totalAssigned = 0;
    for (const r of rows) {
      totalAcked    += r.acked_current;
      totalAssigned += r.total_assigned;
    }
    return {
      policies:      rows.length,
      totalAcked,
      totalAssigned,
      overallRate:   totalAssigned > 0 ? Math.round((totalAcked / totalAssigned) * 100) : 0,
    };
  }, [rows]);

  if (!canView) {
    return (
      <div className="p-6 max-w-[800px] mx-auto">
        <Card>
          <div className="p-6">
            <Shield size={20} className="text-text-muted mb-2"/>
            <div className="font-semibold mb-1">Manager access only</div>
            <div className="text-sm text-text-muted">
              The Policy Registry is for managers and directors. To see your own
              policies, head to <Link to="/policies" className="text-accent">My Policies</Link>.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return <div className="py-20 flex justify-center"><Spinner /></div>;
  }
  if (error) {
    return (
      <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Couldn't load policy registry: {String((error as any)?.message ?? error)}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Policy Registry</h1>
        <div className="text-text-muted text-[13px] mt-1 max-w-[820px]">
          Master register of every Coretel controlled policy. Acknowledgement
          rates are live — every assigned employee × policy with their current
          status. Publish, version, and force-reack actions land in the next
          chunk.
        </div>
      </header>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Policies"        value={String(totals.policies)} />
        <StatCard label="Total assigned"  value={String(totals.totalAssigned)} />
        <StatCard label="Acknowledged" value={String(totals.totalAcked)} />
        <StatCard label="Overall rate"    value={`${totals.overallRate}%`} tone={totals.overallRate >= 95 ? "ok" : totals.overallRate >= 70 ? "warning" : "lost"} />
      </div>

      {/* Registry table */}
      <Card>
        {rows.length === 0 ? (
          <EmptyState
            title="No policies in the register yet"
            description="Once policies are seeded, this table will show every one with its ack rate."
            icon={FileText}
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map(r => <RegistryRow key={r.policy_id} row={r} />)}
          </ul>
        )}
      </Card>

      <div className="mt-4 text-[11px] text-text-dim">
        Click-through per policy (who has/hasn't signed, force re-acknowledge, publish a new version, retire, CSV audit export) lands in T-121 chunk C. Reader + signature flow for employees lands in chunk B.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function StatCard({ label, value, tone }: {
  label: string;
  value: string;
  tone?: "ok" | "warning" | "lost";
}) {
  const toneClass =
    tone === "ok"       ? "text-ok" :
    tone === "warning"  ? "text-warning" :
    tone === "lost"     ? "text-lost" :
    "text-text";
  return (
    <Card>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">{label}</div>
        <div className={`text-xl font-semibold ${toneClass}`}>{value}</div>
      </div>
    </Card>
  );
}

function RegistryRow({ row }: { row: PolicyRow }) {
  const rate = row.total_assigned > 0
    ? Math.round((row.acked_current / row.total_assigned) * 100)
    : 0;
  const tone =
    row.total_assigned === 0 ? "warning" :
    rate === 100 ? "ok" :
    rate >= 70   ? "warning" :
    "lost";
  const label =
    row.total_assigned === 0 ? "no users assigned" :
    rate === 100 ? "all current" :
    `${rate}% acknowledged`;

  return (
    <li className="px-4 py-3 flex items-center gap-3">
      <FileText size={16} className="text-text-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-accent">{row.policy_code}</span>
          {row.current_version_label && (
            <span className="font-mono text-[11px] text-text-dim">
              {row.current_version_label}
            </span>
          )}
          <span className="font-medium truncate">{row.display_name}</span>
        </div>
        <div className="text-[11px] text-text-muted mt-0.5">
          {row.policy_category ?? "—"} · {row.acked_current} of {row.total_assigned} acknowledged · {row.pending_count} pending
        </div>
      </div>
      <Pill label={label} tone={tone as any} />
      </li>
  );
}
